import { useContext, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { FilterContext } from "../../context/FilterContext";
import { InstanceComparisonOptionsProps } from "./statistics-comparison/types";
import FilterDropdown from "../../component/FilterDropdown";
import Filter from "../../types/Filter";
import { closeFilterDropdown, dateForPickerDisplay, dateFromPickerSelection, dateWithTimeZone, getParameter, getTimeZone } from "../../helpers/utils";
import { Dayjs } from "dayjs";

const InstanceComparisonOptions = (props: InstanceComparisonOptionsProps) => {
    const instances = useMemo(() => props.instances, [props.instances]);
    const { filters } = useContext(FilterContext);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const dropdownMenuRef = useRef<HTMLDivElement>(null);

    const [instanceId, setInstanceId] = useState<number>(props.instanceId)
    const [instanceType, setInstanceType] = useState<string | undefined>(props.instanceType)
    const [instanceName, setInstanceName] = useState<string>(props.instanceName)
    const [snapshotId, setSnapshotId] = useState<number | undefined>(props.snapshotId)
    const [periodStart, setPeriodStart] = useState<Date | null>(props.periodStart)
    const [periodEnd, setPeriodEnd] = useState<Date | null>(props.periodEnd)

    const [filter, setFilter] = useState<string>('');
    const [isOpen, setIsOpen] = useState(false);

    const handleClickOutside = (event: any) => {
        if (dropdownMenuRef.current && !dropdownMenuRef.current.contains(event.target)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        if (instanceId && !instanceType) {
            setTimeout(() => {
                setInstanceType(instances.find(i => i.id === instanceId)?.type);
                setInstanceName(instances.find(i => i.id === instanceId)?.name || '');
            }, 0)
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        setPeriodStart(props.periodStart)
    }, [props.periodStart])

    useEffect(() => {
        setPeriodEnd(props.periodEnd)
    }, [props.periodEnd])

    useEffect(() => {
        if (!props.snapshotId && snapshotId !== props.snapshotId) {
            setSnapshotId(props.snapshotId)
        }
    }, [props.snapshotId])

    useEffect(() => {
        props.onOptionsChange({ instanceId, instanceType, snapshotId, periodStart, periodEnd, instanceName })
    }, [instanceId, snapshotId, periodEnd, periodStart, instanceType, instanceName])

    useEffect(() => {
        if (props.instanceId !== -1) {
            setInstanceId(props.instanceId)
            setInstanceType(props.instanceType)
            setInstanceName(props.instanceName)
            setSnapshotId(props.snapshotId)
        }
        else {
            // reset values
            setInstanceId(-1)
            setInstanceType(undefined)
            setInstanceName('')
            setSnapshotId(NaN)
        }
    }, [props.instanceId])

    const getDateFromSnapshot = (parameters: string) => {
        const from: string | null = getParameter(parameters, 'from');
        const to: string | null = getParameter(parameters, 'to');
        const tz: string | null = getParameter(parameters, 'tz');
        const fromDate: Dayjs = dateWithTimeZone(from, tz);
        const toDate: Dayjs = dateWithTimeZone(to, tz);
        return { from: fromDate.toDate(), to: toDate.toDate() }
    }

    function applySnapShot(snapshot: Filter) {
        const parameters = snapshot.parameters
        closeFilterDropdown(props.instanceKey || '', `${props.instanceKey}DropDown`);
        const dateFromSnapshot = getDateFromSnapshot(parameters)
        setPeriodStart(dateFromSnapshot.from)
        setPeriodEnd(dateFromSnapshot.to)
        setSnapshotId(snapshot.id)
    }

    const getFilterName = () => {
        const findFilter = filters.find(item => item.id === snapshotId)
        return findFilter ? ` (${findFilter.name})` : ''
    }

    const toggleDropdown = () => {
        setIsOpen((prev) => !prev);
        searchInputRef.current?.focus();
    };

    function clearFilter() {
        // Clear any pre-existing filter values.
        setFilter('');
    }

    const onInstanceSelect = (id: number) => {
        setInstanceId(id);
        setInstanceType(instances.find(i => i.id === id)?.type);
        setInstanceName(instances.find(i => i.id === id)?.name || '');
        setIsOpen(false);
    };

    const filteredInstances = instances.filter(instance =>
            instance.name.toLowerCase().includes(filter.toLowerCase())
    );

    const timezone = getTimeZone();

    return <div className="row row-cols-lg-12 mt-3 comparison-filter" key={props.instanceId}>
        <div className='col-xs-34 col-md-3 col-lg-3 comparison-filter-instance'>
            <span className="required">Instance</span><br/>
            <button type="button" id="filter" className="form-select text-start" onClick={toggleDropdown}
                    aria-expanded="false">
                {instanceName || 'Select instance'}
            </button>
            <div id="dropdown-menu" className={`row g-0 dropdown-menu dropdown-menu-scroll ${isOpen ? "show" : ""}`}
                 aria-labelledby="filter"
                 ref={dropdownMenuRef} onClick={(e) => e.stopPropagation()}>
                <ul className="col">
                    <li className="search">
                        <div className="row g-0">
                            <div className="col-9">
                                <input type="text" autoFocus className="form-control"
                                       placeholder="Search Instance"
                                       value={filter}
                                       onChange={(e) => setFilter(e.target.value)}
                                       ref={searchInputRef}/>
                            </div>
                            <div className="col-3 text-end">
                                <button type="button" className="btn btn-small btn-dark" onClick={clearFilter}>
                                    <i className="far fa-undo" aria-hidden="true"></i>
                                </button>
                            </div>
                        </div>
                    </li>
                    <li>
                        <hr className="dropdown-divider"/>
                    </li>

                    {filteredInstances.length > 0 ? (
                            filteredInstances.map((item) => (
                                    <li key={item.id || 0} data-bs-dismiss="dropdown">
                                    <span className='dropdown-item' onClick={() => onInstanceSelect(item.id)}>
                                        {item.name}
                                    </span>
                                    </li>
                            ))
                    ) : (
                            <li className="dropdown-item text-muted">No results found</li>
                    )}
                </ul>
            </div>
        </div>
        <div className='col-xs-3 col-md-3 col-lg-3'>
            <span className="required"><label htmlFor="period-start" className="reminder"
                                              data-tooltip-id="global-tooltip" data-tooltip-content="The start time to compare against">Period Start</label></span><br/>
            <DatePicker
                    name="period-start"
                    className={`form-control w-100`}
                    dateFormat="yyyy/MM/dd HH:mm"
                    placeholderText="yyyy/MM/dd"
                    disabled={props.loading}
                    popperPlacement="auto"
                    showTimeSelect
                    timeFormat="HH:mm"
                    maxDate={null}
                    onChange={(date: Date | null) => {
                        setSnapshotId(NaN)
                        setPeriodStart(dateFromPickerSelection(date, timezone))
                    }
                    }
                    selected={dateForPickerDisplay(periodStart, timezone)}
                    shouldCloseOnSelect={true}/>
        </div>
        <div className='col-xs-3 col-md-3 col-lg-3'>
            <span className="required"><label htmlFor="period-end" className="reminder"
                                              data-tooltip-id="global-tooltip" data-tooltip-content="The end time to compare against">Period End</label></span><br/>
            <DatePicker
                    name="period-end"
                    className={`form-control w-100`}
                    dateFormat="yyyy/MM/dd HH:mm"
                    placeholderText="yyyy/MM/dd"
                    popperPlacement="auto"
                    disabled={props.loading}
                    showTimeSelect
                    timeFormat="HH:mm"
                    maxDate={null}
                    onChange={(date: Date | null) => {
                        setPeriodEnd(dateFromPickerSelection(date, timezone))
                        setSnapshotId(NaN)
                    }
                    }
                    selected={dateForPickerDisplay(periodEnd, timezone)}
                    shouldCloseOnSelect={true}/>
        </div>


        <div className="col-xs-3 col-md-3 col-lg-3">
            <label>Snapshot {getFilterName()}</label>
            <div className="col-xs-12 col-md-4 col-lg-2">

                <FilterDropdown filterId={props.instanceKey || ''} dropdownId={`${props.instanceKey}DropDown`}
                                dataTip={`Choose snapshot for ${props.instanceKey === 'firstInstanceOptions' ? 'first' : 'second'} instance`}
                                applyFilter={applySnapShot}
                />
            </div>

        </div>

    </div>
}

export default InstanceComparisonOptions;
